import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaCogs,
  FaRobot,
  FaLaptopCode,
  FaCheckCircle,
  FaIndustry,
  FaMicrochip,
  FaCar,
  FaWarehouse,
  FaArrowRight,
  FaClipboardCheck,
  FaPencilRuler,
  FaTools,
  FaFlask,
  FaRocket,
  FaTachometerAlt,
  FaFileAlt,
  FaUserCog,
  FaAward
} from 'react-icons/fa';
import './Home.css';

const Home = () => {

  useEffect(() => {
    // Add scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .fade-up, .scale-up, .slide-in-left, .slide-in-right, .slide-in-blur-left, .slide-in-blur-right');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const coreVerticals = [
    {
      icon: <FaCogs />,
      title: "Industrial Automation & Control Systems",
      description: "We automate processes with robust control architectures using PLCs, SCADA, IIoT gateways, servo systems, and custom control panels — enabling consistent production, reduced manual dependency, and improved operational reliability."
    },
    {
      icon: <FaRobot />,
      title: "Robotics, SPMs & Test Engineering",
      description: "We build special-purpose machines, integrate industrial robots, and engineer high-precision PCB Test Jigs (ICT/FCT) for zero-defect electronic manufacturing. Our systems accelerate throughput, ensure repeatability, and eliminate human error."
    },
    {
      icon: <FaLaptopCode />,
      title: "Industrial Software & Smart Manufacturing",
      description: "We develop custom software platforms that collect, analyze, and visualize real-time industrial data, including IoT dashboards, OEE/MES systems, workflow tools, and cloud-based monitoring solutions — turning insights into action."
    }
  ];

  const whyChooseUs = [
    { text: "Full-stack capability — Machines, electronics, control, and software under one roof" },
    { text: "Outcome-driven approach — We focus on efficiency, ROI, and measurable improvements" },
    { text: "Custom-built solutions — Tailored to your process, industry, and production goals" },
    { text: "Faster execution — Agile development with minimal downtime for your operations" },
    { text: "Industry-aligned expertise — Proven solutions across electronics, manufacturing, and process industries" }
  ];

  const pcbFeatures = [
    { icon: <FaTachometerAlt />, text: "Faster test cycles and improved throughput" },
    { icon: <FaTools />, text: "Reliable fixture design with durable mechanics" },
    { icon: <FaFileAlt />, text: "Automated reporting and test analytics" },
    { icon: <FaUserCog />, text: "Reduced operator dependency and error-free results" }
  ];

  const processSteps = [
    { number: "01", title: "Understand & Evaluate", description: "Requirement analysis and feasibility study", icon: <FaClipboardCheck /> },
    { number: "02", title: "Design & Engineer", description: "Mechanical, electrical, robotic & software design", icon: <FaPencilRuler /> },
    { number: "03", title: "Build & Integrate", description: "In-house fabrication and system integration", icon: <FaTools /> },
    { number: "04", title: "Test & Validate", description: "Real-world performance testing and fine-tuning", icon: <FaFlask /> },
    { number: "05", title: "Deploy & Support", description: "Installation, training, and long-term maintenance", icon: <FaRocket /> }
  ];

  const industries = [
    { icon: <FaMicrochip />, name: "Electronics & PCB Manufacturing" },
    { icon: <FaCar />, name: "Automotive Component Manufacturing" },
    { icon: <FaIndustry />, name: "Machinery & Fabrication Units" },
    { icon: <FaWarehouse />, name: "Process & Material Handling Industries" }
  ];

  return (
    <div className="home">
      <SEO
        title="Industrial Automation, IoT Solutions & PCB Test Jigs | Manufacturing Excellence Since 2016"
        description="Robolog Automation delivers turnkey industrial automation solutions including robotics, SCADA systems, IoT platforms, custom PCB test jigs (ICT/FCT), and smart manufacturing software. Transform your production with 8+ years of expertise serving 70+ customers across electronics, automotive, and process industries."
        keywords="robolog automation, industrial automation, IoT solutions, PCB test jigs, ICT test fixtures, FCT test jigs, SCADA systems, PLC programming, industrial robotics, manufacturing automation, smart factory solutions, IIoT gateway, industrial software development, test automation equipment, electronics manufacturing, robolog, automation company India, industrial control systems, MES systems, OEE monitoring, production automation, custom test jigs, automated testing solutions, industrial IoT platform"
        ogImage="/Home-page.png"
      />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="fade-up">
            Turnkey Industrial Automation for <span className="text-highlight">Smarter Manufacturing</span>
          </h1>
          <p className="hero-subtitle fade-up">
            We design and deliver end-to-end automation solutions — from custom machines and robotics
            to intelligent industrial software — enabling manufacturers to improve productivity,
            accuracy, and real-time decision-making.
          </p>
          <div className="hero-image scale-up">
            <img src="/Home-page.png" alt="Industrial Automation Solutions by Robolog" />
          </div>
        </div>
        <div className="hero-decoration">
          <div className="hero-shape hero-shape-1"></div>
          <div className="hero-shape hero-shape-2"></div>
          <div className="hero-shape hero-shape-3"></div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="section who-we-are">
        <div className="container">
          <div className="who-we-are-content slide-in-blur-left">
            <div className="badge">Since 2016</div>
            <h2 className="section-title">Who We Are</h2>
            <p className="who-we-are-text">
              Founded in 2016, <strong>Robolog</strong> is a solution-driven industrial automation company
              helping manufacturers transform manual, time-consuming operations into intelligent, data-driven systems.
              We combine engineering expertise, custom machine development, robotics integration, and industrial
              software platforms to deliver <strong>measurable business outcomes</strong> — not just technology.
            </p>
            <Link to="/about" className="btn btn-outline">
              Learn More About Us
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Core Verticals Section */}
      <section className="section section-alt core-verticals">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">What We Do</h2>
            <p className="section-subtitle">
              Our core verticals delivering end-to-end automation solutions
            </p>
          </div>

          <div className="verticals-grid">
            {coreVerticals.map((vertical, index) => (
              <div key={index} className={`vertical-card fade-up`} style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="vertical-icon">
                  {vertical.icon}
                </div>
                <h3>{vertical.title}</h3>
                <p>{vertical.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Robolog Section */}
      <section className="section why-choose">
        <div className="container">
          <div className="why-choose-wrapper">
            <div className="why-choose-content slide-in-blur-left">
              <h2 className="section-title">Why Choose Robolog?</h2>
              <ul className="benefits-list">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="benefit-item">
                    <FaCheckCircle className="benefit-icon" />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="why-choose-visual scale-up">
              <div className="stats-showcase">
                <div className="stat-box">
                  <h3>100+</h3>
                  <p>Projects Delivered</p>
                </div>
                <div className="stat-box">
                  <h3>70+</h3>
                  <p>Happy Customers</p>
                </div>
                <div className="stat-box highlight">
                  <h3>8+</h3>
                  <p>Years Experience</p>
                </div>
                <div className="stat-box">
                  <FaAward className="stat-icon" />
                  <p>Quality Assured</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PCB Test Jigs Section */}
      <section className="section section-alt pcb-section">
        <div className="container">
          <div className="pcb-wrapper">
            <div className="pcb-content slide-in-blur-left">
              <div className="badge">Specialized Expertise</div>
              <h2 className="section-title">PCB Test Jigs</h2>
              <p className="pcb-description">
                We design and manufacture advanced <strong>ICT and FCT test jigs</strong> for PCB validation,
                delivering precision testing solutions for electronics manufacturers.
              </p>
              <div className="pcb-features">
                {pcbFeatures.map((feature, index) => (
                  <div key={index} className="pcb-feature">
                    <div className="pcb-feature-icon">{feature.icon}</div>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
              <p className="pcb-cta-text">
                This capability positions us as a trusted partner for electronics manufacturers
                seeking scalable, automated QC infrastructure.
              </p>
              <Link to="/services" className="btn btn-primary">
                Learn More
                <FaArrowRight />
              </Link>
            </div>
            <div className="pcb-visual scale-up">
              <div className="pcb-image-placeholder">
                <FaMicrochip />
                <span>ICT/FCT Testing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section process-section">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">How We Work</h2>
            <p className="section-subtitle">
              Our proven process ensures successful project delivery every time
            </p>
          </div>

          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <div key={index} className={`process-step fade-up`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="step-icon">
                  {step.icon}
                </div>
                <div className="step-number">{step.number}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section section-alt industries-section">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-subtitle">
              Delivering automation excellence across diverse manufacturing sectors
            </p>
          </div>

          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className={`industry-card fade-up`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="industry-icon">
                  {industry.icon}
                </div>
                <h4>{industry.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content scale-up">
            <h2>Let's Automate Your Next Breakthrough</h2>
            <p className="cta-tagline">
              Reduce costs. Unlock productivity. Scale intelligently.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Request a Consultation
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-outline btn-lg">
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
